/**
 * PlayerCard Component
 * Displays individual player information with hover effects
 */

import { motion } from 'framer-motion';
import type { MissingPlayer, Position } from '@/types';

interface PlayerCardProps {
  player: MissingPlayer;
  onClick?: (player: MissingPlayer) => void;
}

const positionColors: Record<Position, string> = {
  TOP: '#ff6b6b',
  JUNGLE: '#4ecdc4',
  MID: '#ffe66d',
  ADC: '#ff6b9d',
  SUPPORT: '#95e1d3',
};

const positionNames: Record<Position, string> = {
  TOP: '上单',
  JUNGLE: '打野',
  MID: '中单',
  ADC: 'ADC',
  SUPPORT: '辅助',
};

export function PlayerCard({ player, onClick }: PlayerCardProps) {
  const position = player.position as Position;
  const positionColor = positionColors[position] || '#a0a0b0';

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -4 }}
      whileTap={{ scale: 0.98 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onClick={() => onClick?.(player)}
      className="bg-surface border border-border rounded-2xl overflow-hidden cursor-pointer group"
    >
      {/* Player Photo */}
      <div className="relative h-56 overflow-hidden bg-background">
        {player.photo_url ? (
          <img
            src={player.photo_url}
            alt={player.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-6xl font-bold text-tertiary/30">
              {player.name_en.charAt(0)}
            </span>
          </div>
        )}

        {/* Position Badge */}
        {position && (
          <div
            className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold text-background"
            style={{ backgroundColor: positionColor }}
          >
            {position}
          </div>
        )}

        {/* Status Overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-surface to-transparent" />
      </div>

      {/* Player Info */}
      <div className="p-5">
        <h3 className="text-xl font-bold text-primary mb-1">
          {player.name_en}
        </h3>
        <p className="text-secondary text-sm mb-3">{player.name}</p>

        {/* Team Info */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-tertiary">2025</span>
            <span className="text-secondary font-medium">
              {player.team_2025 || '自由人'}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-tertiary">2026</span>
            <span className="text-status-retired font-medium">离场</span>
          </div>
        </div>

        {/* Position Tag */}
        {position && (
          <div className="mt-4 pt-3 border-t border-border">
            <span
              className="text-xs font-medium px-2 py-1 rounded"
              style={{
                backgroundColor: `${positionColor}20`,
                color: positionColor,
              }}
            >
              {positionNames[position]}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
}
